import {filterableDatabaseSettingsSchema} from "@AppBuilderLib/entities/parameter/lib/filterableDatabase/filterableDatabaseSettingsSchema";
import {
	IAppBuilderActionPropsAddToCartSchema,
	IAppBuilderActionPropsCameraSchema,
	IAppBuilderActionPropsCommonSchema,
	IAppBuilderActionPropsCreateModelStateSchema,
	IAppBuilderActionPropsImportModelStateSchema,
	IAppBuilderActionPropsRedoSchema,
	IAppBuilderActionPropsResetParameterValuesSchema,
	IAppBuilderActionPropsSetParameterValueSchema,
	IAppBuilderActionPropsSetParameterValuesSchema,
	IAppBuilderActionPropsSoundSchema,
	IAppBuilderActionPropsUndoSchema,
	IAppBuilderIconSchema,
	IAppBuilderParameterValueSourceDefinitionSchema,
} from "@AppBuilderLib/features/appbuilder/config/appbuilderActionsTypecheck";
import {preprocessActionDefinitionInput} from "@AppBuilderLib/features/appbuilder/lib/legacyActionToDefinition";
import {prettifyError, z} from "@AppBuilderLib/shared/lib/zod";
import {appBuilderThemeOtherPropsSchema} from "@AppBuilderLib/shared/mantine-props/appBuilderThemeOther.zod";
import {mantineThemeOverridePropsSchema} from "@AppBuilderLib/shared/mantine-props/themeOverride.zod";
import {
	ResParameterType,
	ResStructureType,
	ResVisualizationType,
} from "@shapediver/sdk.geometry-api-sdk-v2";
import {
	ATTRIBUTE_VISUALIZATION,
	TAG3D_JUSTIFICATION,
} from "@shapediver/viewer.shared.types";
import {
	AppBuilderActionType,
	AppBuilderContainerNameType,
	AttributeVisualizationVisibility,
	FormWidgetSubmitBehavior,
	IAppBuilderActionDefinition,
	IAppBuilderWidget,
	ParameterStringInputMode,
	SavedStatesVisualization,
	SelectComponentType,
} from "./appbuilder";
import {APP_BUILDER_CUSTOM_EVENT_PATTERN} from "./appbuilderActionSlots";
import {
	appBuilderStandardContainerPropsSchema,
	appBuilderViewportAnchorMobileFallbackSchema,
} from "./appbuilderMobileFallback";
import {GenericToolName} from "./appbuilderagent";
import {validateThemeComponentsRecord} from "./validateThemeComponentsRecord";

import {JsonValueSchema} from "./jsonValue";

// Zod schema for MantineThemeComponent (classNames, styles, vars, defaultProps are opaque JSON values)
const MantineThemeComponentSchema = z.strictObject({
	classNames: JsonValueSchema.optional(),
	styles: JsonValueSchema.optional(),
	vars: JsonValueSchema.optional(),
	defaultProps: JsonValueSchema.optional(),
});

export type MantineThemeComponentSchemaOutput = z.infer<
	typeof MantineThemeComponentSchema
>;

// Hand-written strict schema for top-level `themeOverrides` in settings JSON.
// Doc mirror / nested component prop: `MantineThemeOverrideProps` in
// `shared/mantine-props/themeOverride.schema-input.ts` (generated `mantineThemeOverridePropsSchema` — looser `components` record).
// variantColorResolver is a function and cannot appear in JSON config, so it is excluded here.
const MantineThemeFullSchema = z.strictObject({
	focusRing: z.enum(["auto", "always", "never"]),
	scale: z.number(),
	fontSmoothing: z.boolean(),
	white: z.string(),
	black: z.string(),
	primaryColor: z.string(),
	autoContrast: z.boolean(),
	luminanceThreshold: z.number(),
	fontFamily: z.string(),
	fontFamilyMonospace: z.string(),
	defaultRadius: z.union([z.string(), z.number()]),
	cursorType: z.enum(["default", "pointer"]),
	respectReducedMotion: z.boolean(),
	activeClassName: z.string(),
	focusClassName: z.string(),
	colors: z.record(z.string(), z.array(z.string()).min(10)),
	primaryShade: z.union([
		z.number().int().min(0).max(9),
		z.strictObject({
			light: z.number().int().min(0).max(9),
			dark: z.number().int().min(0).max(9),
		}),
	]),
	fontSizes: z.record(z.string(), z.string()),
	lineHeights: z.record(z.string(), z.string()),
	fontWeights: z.record(z.string(), z.string()),
	radius: z.record(z.string(), z.string()),
	spacing: z.record(z.string(), z.string()),
	breakpoints: z.record(z.string(), z.string()),
	shadows: z.record(z.string(), z.string()),
	headings: z.strictObject({
		fontFamily: z.string(),
		fontWeight: z.string(),
		textWrap: z.enum(["wrap", "nowrap", "balance", "pretty", "stable"]),
		sizes: z.strictObject({
			h1: z.strictObject({
				fontSize: z.string(),
				fontWeight: z.string().optional(),
				lineHeight: z.string(),
			}),
			h2: z.strictObject({
				fontSize: z.string(),
				fontWeight: z.string().optional(),
				lineHeight: z.string(),
			}),
			h3: z.strictObject({
				fontSize: z.string(),
				fontWeight: z.string().optional(),
				lineHeight: z.string(),
			}),
			h4: z.strictObject({
				fontSize: z.string(),
				fontWeight: z.string().optional(),
				lineHeight: z.string(),
			}),
			h5: z.strictObject({
				fontSize: z.string(),
				fontWeight: z.string().optional(),
				lineHeight: z.string(),
			}),
			h6: z.strictObject({
				fontSize: z.string(),
				fontWeight: z.string().optional(),
				lineHeight: z.string(),
			}),
		}),
	}),
	defaultGradient: z.strictObject({
		from: z.string(),
		to: z.string(),
		deg: z.number().optional(),
	}),
	components: z.record(z.string(), MantineThemeComponentSchema),
	other: appBuilderThemeOtherPropsSchema,
	// variantColorResolver is a function — excluded from JSON config schema
});

export type MantineThemeFullSchemaOutput = z.infer<
	typeof MantineThemeFullSchema
>;

export type MantineThemeOverridePropsSchemaOutput = z.infer<
	typeof mantineThemeOverridePropsSchema
>;

// Partial version used for themeOverrides in config files (matches MantineThemeOverride = PartialDeep<MantineTheme>)
export const MantineThemeOverrideSchema = MantineThemeFullSchema.partial();

// Zod type definition for SelectComponentType
const selectComponentTypes = [
	"buttonflex",
	"buttongroup",
	"chipgroup",
	"dropdown",
	"color",
	"imagedropdown",
	"fullwidthcards",
	"carousel",
	"grid",
	"multiselect-checkboxes",
] as const satisfies readonly SelectComponentType[];

const SelectComponentTypeSchema = z.enum(selectComponentTypes);

// Zod type definition for ISelectComponentItemDataType
export const ISelectComponentItemDataTypeSchema = z.object({
	displayname: z.string().optional(),
	tooltip: z.string().optional(),
	description: z.string().optional(),
	imageUrl: z.string().optional(),
	color: z.string().optional(),
	hidden: z.boolean().optional(),
	data: z.record(z.string(), z.any()).optional(),
});

// Zod type definition for ISelectParameterSettings
const ISelectParameterSettingsSchema = z
	.object({
		type: SelectComponentTypeSchema.optional(),
		itemData: z
			.record(z.string(), ISelectComponentItemDataTypeSchema)
			.optional(),
		searchable: z.boolean().optional(),
		limit: z.int().positive().optional(),
		height: z.string().optional(),
		database: filterableDatabaseSettingsSchema.optional(),
	})
	.refine(
		(s) => !s.database || s.type === "fullwidthcards" || s.type === "grid",
		{
			message:
				'database requires selectSettings.type "fullwidthcards" or "grid"',
		},
	);

export const validateSelectParameterSettings = (value: any) => {
	return ISelectParameterSettingsSchema.safeParse(value);
};

// Zod type definition for IStringParameterSelectSettings
const IStringParameterSelectSettingsSchema =
	ISelectParameterSettingsSchema.safeExtend({
		items: z.array(z.string()).optional(),
		source: z.string().optional(),
	});

// Zod type definition for IStringParameterSettings
const IStringParameterSettingsSchema = z.object({
	lines: z.int().positive().optional(),
	debounce: z.int().nonnegative().optional(),
	mode: z.enum(ParameterStringInputMode).optional(),
	selectSettings: IStringParameterSelectSettingsSchema.optional(),
});

export const validateStringParameterSettings = (value: any) => {
	return IStringParameterSettingsSchema.safeParse(value);
};

// Zod type definition for INumberParameterSettings
const INumberParameterSettingsSchema = z.object({
	step: z.number().positive().optional(),
	marks: z
		.array(
			z.object({
				value: z.number(),
				label: z.string().optional(),
			}),
		)
		.optional(),
	restrictToMarks: z.boolean().optional(),
	min: z.number().optional(),
	max: z.number().optional(),
});

export const validateNumberParameterSettings = (value: any) => {
	if (value === undefined || value === null)
		return {success: false as const, error: undefined};
	return INumberParameterSettingsSchema.safeParse(value);
};

// Zod type definition for IAppBuilderParameterDefinition
const IAppBuilderParameterDefinitionSchema = z.strictObject({
	id: z.string(),
	choices: z.array(z.string()).optional(),
	decimalplaces: z.number().optional(),
	defval: z.string(),
	expression: z.string().optional(),
	format: z.array(z.string()).optional(),
	min: z.number().optional(),
	max: z.number().optional(),
	umin: z.number().optional(),
	umax: z.number().optional(),
	vmin: z.number().optional(),
	vmax: z.number().optional(),
	interval: z.number().optional(),
	name: z.string(),
	type: z.enum(ResParameterType),
	visualization: z.enum(ResVisualizationType).optional(),
	structure: z.enum(ResStructureType).optional(),
	group: z
		.strictObject({
			id: z.string(),
			name: z.string(),
		})
		.optional(),
	hint: z.string().optional(),
	order: z.number().optional(),
	tooltip: z.string().optional(),
	displayname: z.string().optional(),
	hidden: z.boolean(),
	settings: z.record(z.string(), JsonValueSchema).optional(),
	value: z.string().optional(),
	step: z.number().positive().optional(),
});

// Zod type definition for property "overrides" of IAppBuilderParameterRef
const IAppBuilderParameterOverridesSchema =
	IAppBuilderParameterDefinitionSchema.partial().pick({
		displayname: true,
		group: true,
		order: true,
		tooltip: true,
		hidden: true,
		settings: true,
		step: true,
	});

// Zod type definition for IAppBuilderParameterRef
const IAppBuilderParameterRefSchema = z.strictObject({
	name: z.string(),
	sessionId: z.string().optional(),
	overrides: IAppBuilderParameterOverridesSchema.optional(),
	disableIfDirty: z.boolean().optional(),
	acceptRejectMode: z.boolean().optional(),
	acceptRejectModePresentation: z.enum(["global", "inline"]).optional(),
});

// Zod type definition for property "overrides" of IAppBuilderExportRef
const IAppBuilderExportOverridesSchema = IAppBuilderParameterOverridesSchema;

// Zod type definition for IAppBuilderExportRef
const IAppBuilderExportRefSchema = z.strictObject({
	name: z.string(),
	sessionId: z.string().optional(),
	overrides: IAppBuilderExportOverridesSchema.optional(),
});

// Zod type definition for IAppBuilderImageRef
export const IAppBuilderImageRefSchema = z.strictObject({
	export: IAppBuilderExportRefSchema.pick({
		name: true,
		sessionId: true,
	}).optional(),
	href: z.string().optional(),
});

// Zod type definition for IAppBuilderLegacyActionPropsCreateModelState
const IAppBuilderLegacyActionPropsCreateModelStateSchema =
	IAppBuilderActionPropsCreateModelStateSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderLegacyActionPropsAddToCart
const IAppBuilderLegacyActionPropsAddToCartSchema =
	IAppBuilderActionPropsAddToCartSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderLegacyActionPropsSetParameterValue
const IAppBuilderLegacyActionPropsSetParameterValueSchema =
	IAppBuilderActionPropsSetParameterValueSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderLegacyActionPropsSetParameterValues
const IAppBuilderLegacyActionPropsSetParameterValuesSchema =
	IAppBuilderActionPropsSetParameterValuesSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsSetBrowserLocation
const IAppBuilderActionPropsSetBrowserLocationSchema = z.strictObject({
	href: z.string().optional(),
	pathname: z.string().optional(),
	search: z.string().optional(),
	hash: z.string().optional(),
	target: z.enum(["_self", "_blank", "_parent", "_top"]).optional(),
});
// Zod type definition for IAppBuilderLegacyActionPropsSetBrowserLocation
const IAppBuilderLegacyActionPropsSetBrowserLocationSchema =
	IAppBuilderActionPropsSetBrowserLocationSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsCloseConfigurator
const IAppBuilderActionPropsCloseConfigurator = z.strictObject({});

// Zod type definition for IAppBuilderLegacyActionPropsCloseConfigurator
const IAppBuilderLegacyActionPropsCloseConfiguratorSchema =
	IAppBuilderActionPropsCloseConfigurator.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsAr
const IAppBuilderActionPropsArSchema = z.strictObject({
	viewportId: z.string().optional(),
});

// Zod type definition for IAppBuilderLegacyActionPropsAr
const IAppBuilderLegacyActionPropsArSchema =
	IAppBuilderActionPropsArSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsFullscreen
const IAppBuilderActionPropsFullscreenSchema = z.strictObject({
	type: z.enum(["fullscreen", "fullscreen3States"]).optional(),
	fullscreenId: z.string().optional(),
});

// Zod type definition for IAppBuilderLegacyActionPropsFullscreen
const IAppBuilderLegacyActionPropsFullscreenSchema =
	IAppBuilderActionPropsFullscreenSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderLegacyActionPropsUndo
const IAppBuilderLegacyActionPropsUndoSchema =
	IAppBuilderActionPropsUndoSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderLegacyActionPropsRedo
const IAppBuilderLegacyActionPropsRedoSchema =
	IAppBuilderActionPropsRedoSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderLegacyActionPropsResetParameterValues
const IAppBuilderLegacyActionPropsResetParameterValuesSchema =
	IAppBuilderActionPropsResetParameterValuesSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsImportParameterValues
const IAppBuilderActionPropsImportParameterValuesSchema = z.strictObject({});

// Zod type definition for IAppBuilderLegacyActionPropsImportParameterValues
const IAppBuilderLegacyActionPropsImportParameterValuesSchema =
	IAppBuilderActionPropsImportParameterValuesSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsExportParameterValues
const IAppBuilderActionPropsExportParameterValuesSchema = z.strictObject({});

// Zod type definition for IAppBuilderLegacyActionPropsExportParameterValues
const IAppBuilderLegacyActionPropsExportParameterValuesSchema =
	IAppBuilderActionPropsExportParameterValuesSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderLegacyActionPropsImportModelState
const IAppBuilderLegacyActionPropsImportModelStateSchema =
	IAppBuilderActionPropsImportModelStateSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderLegacyActionPropsSound
const IAppBuilderLegacyActionPropsSoundSchema =
	IAppBuilderActionPropsSoundSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

const IAppBuilderActionPropsSetContainerVisibilitySchema = z.strictObject({
	// The full container schema is recursive through action widgets, so validate
	// the discriminator and identity fields needed by this action here.
	container: z.union([
		z.looseObject({
			name: z.enum([
				AppBuilderContainerNameType.Left,
				AppBuilderContainerNameType.Right,
				AppBuilderContainerNameType.Top,
				AppBuilderContainerNameType.Bottom,
			]),
		}),
		z.looseObject({
			name: z.enum([
				AppBuilderContainerNameType.Anchor2d,
				AppBuilderContainerNameType.Anchor3d,
				AppBuilderContainerNameType.Toolbar,
			]),
			props: z.looseObject({id: z.string()}),
		}),
	]),
	mode: z.enum(["open", "close", "toggle"]),
});

const IAppBuilderLegacyActionPropsSetContainerVisibilitySchema =
	IAppBuilderActionPropsSetContainerVisibilitySchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderActionPropsMessageToParent
const IAppBuilderActionPropsMessageToParentSchema = z.strictObject({
	type: z.string(),
	data: z.record(z.string(), JsonValueSchema).optional(),
});

// Zod type definition for IAppBuilderLegacyActionPropsMessageToParent
const IAppBuilderLegacyActionPropsMessageToParentSchema =
	IAppBuilderActionPropsMessageToParentSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Assigned after IAppBuilderActionDefinitionSchemaBase so nested executeActions
// can recursively validate IAppBuilderActionDefinition values.
// eslint-disable-next-line prefer-const -- declaration must precede the recursive schema body
let IAppBuilderActionDefinitionSchema: z.ZodType<IAppBuilderActionDefinition>;

// Zod type definition for IAppBuilderActionPropsExecuteActions
const IAppBuilderActionPropsExecuteActionsSchema = z.strictObject({
	actions: z.array(z.lazy(() => IAppBuilderActionDefinitionSchema)),
	mode: z.enum(["parallel", "sequential"]).optional(),
});

// Zod type definition for IAppBuilderLegacyActionPropsExecuteActions
const IAppBuilderLegacyActionPropsExecuteActionsSchema =
	IAppBuilderActionPropsExecuteActionsSchema.extend(
		IAppBuilderActionPropsCommonSchema.shape,
	);

// Zod type definition for IAppBuilderLegacyActionDefinition
const IAppBuilderLegacyActionDefinitionSchema = z.discriminatedUnion("type", [
	z.strictObject({
		type: z.literal(AppBuilderActionType.CreateModelState),
		props: IAppBuilderLegacyActionPropsCreateModelStateSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.AddToCart),
		props: IAppBuilderLegacyActionPropsAddToCartSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.SetParameterValue),
		props: IAppBuilderLegacyActionPropsSetParameterValueSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.SetParameterValues),
		props: IAppBuilderLegacyActionPropsSetParameterValuesSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.SetBrowserLocation),
		props: IAppBuilderLegacyActionPropsSetBrowserLocationSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.CloseConfigurator),
		props: IAppBuilderLegacyActionPropsCloseConfiguratorSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.Ar),
		props: IAppBuilderLegacyActionPropsArSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.Fullscreen),
		props: IAppBuilderLegacyActionPropsFullscreenSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.Undo),
		props: IAppBuilderLegacyActionPropsUndoSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.Redo),
		props: IAppBuilderLegacyActionPropsRedoSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.ResetParameterValues),
		props: IAppBuilderLegacyActionPropsResetParameterValuesSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.ImportParameterValues),
		props: IAppBuilderLegacyActionPropsImportParameterValuesSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.ExportParameterValues),
		props: IAppBuilderLegacyActionPropsExportParameterValuesSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.ImportModelState),
		props: IAppBuilderLegacyActionPropsImportModelStateSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.Camera),
		props: IAppBuilderActionPropsCameraSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.Sound),
		props: IAppBuilderLegacyActionPropsSoundSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.SetContainerVisibility),
		props: IAppBuilderLegacyActionPropsSetContainerVisibilitySchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.MessageToParent),
		props: IAppBuilderLegacyActionPropsMessageToParentSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.ExecuteActions),
		props: IAppBuilderLegacyActionPropsExecuteActionsSchema,
	}),
]);

// Zod type definition for property "overrides" of IAppBuilderControlParameterRef
const IAppBuilderControlParameterRefOverridesSchema =
	IAppBuilderParameterDefinitionSchema.partial().pick({
		displayname: true,
		tooltip: true,
		hidden: true,
		settings: true,
		step: true,
	});

// Zod type definition for property "overrides" of IAppBuilderControlExportRef
const IAppBuilderControlExportRefOverridesSchema =
	IAppBuilderParameterDefinitionSchema.partial().pick({
		displayname: true,
		tooltip: true,
		hidden: true,
	});

// Zod type definition for property "overrides" of IAppBuilderControlOutputRef
const IAppBuilderControlOutputRefOverridesSchema =
	IAppBuilderParameterDefinitionSchema.partial().pick({
		displayname: true,
		tooltip: true,
		hidden: true,
	});

const IAppBuilderControlPresentationSchema = z.strictObject({
	label: z.string().optional(),
	icon: IAppBuilderIconSchema.optional(),
	tooltip: z.string().optional(),
	labelSide: z.enum(["top", "bottom", "left", "right"]).optional(),
	labelAlign: z.enum(["start", "center", "end"]).optional(),
});

// Zod type definition for IAppBuilderControlParameterRef
const IAppBuilderControlParameterRefSchema = z
	.strictObject({
		name: z.string(),
		sessionId: z.string().optional(),
		overrides: IAppBuilderControlParameterRefOverridesSchema.optional(),
		disableIfDirty: z.boolean().optional(),
		acceptRejectMode: z.boolean().optional(),
		acceptRejectModePresentation: z.enum(["global", "inline"]).optional(),
		// Default preserves compatibility with controls created before delegates were introduced.
		delegates: z
			.array(
				z.strictObject({
					name: z.string(),
					sessionId: z.string().optional(),
				}),
			)
			.default([]),
	})
	.extend(IAppBuilderControlPresentationSchema.shape);

// Zod type definition for IAppBuilderControlExportRef
const IAppBuilderControlExportRefSchema = z
	.strictObject({
		name: z.string(),
		sessionId: z.string().optional(),
		overrides: IAppBuilderControlExportRefOverridesSchema.optional(),
		parameterValues: z
			.array(IAppBuilderLegacyActionPropsSetParameterValueSchema)
			.optional(),
	})
	.extend(IAppBuilderControlPresentationSchema.shape);

// Zod type definition for IAppBuilderActionDefinition
const IAppBuilderActionDefinitionSchemaBase = z.discriminatedUnion("type", [
	z.strictObject({
		type: z.literal(AppBuilderActionType.CreateModelState),
		props: IAppBuilderActionPropsCreateModelStateSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.AddToCart),
		props: IAppBuilderActionPropsAddToCartSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.SetParameterValue),
		props: IAppBuilderActionPropsSetParameterValueSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.SetParameterValues),
		props: IAppBuilderActionPropsSetParameterValuesSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.SetBrowserLocation),
		props: IAppBuilderActionPropsSetBrowserLocationSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.CloseConfigurator),
		props: IAppBuilderActionPropsCloseConfigurator,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.Ar),
		props: IAppBuilderActionPropsArSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.Fullscreen),
		props: IAppBuilderActionPropsFullscreenSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.Undo),
		props: IAppBuilderActionPropsUndoSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.Redo),
		props: IAppBuilderActionPropsRedoSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.ResetParameterValues),
		props: IAppBuilderActionPropsResetParameterValuesSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.ImportParameterValues),
		props: IAppBuilderActionPropsImportParameterValuesSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.ExportParameterValues),
		props: IAppBuilderActionPropsExportParameterValuesSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.ImportModelState),
		props: IAppBuilderActionPropsImportModelStateSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.Camera),
		props: IAppBuilderActionPropsCameraSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.Sound),
		props: IAppBuilderActionPropsSoundSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.SetContainerVisibility),
		props: IAppBuilderActionPropsSetContainerVisibilitySchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.MessageToParent),
		props: IAppBuilderActionPropsMessageToParentSchema,
	}),
	z.strictObject({
		type: z.literal(AppBuilderActionType.ExecuteActions),
		props: IAppBuilderActionPropsExecuteActionsSchema,
	}),
]);

IAppBuilderActionDefinitionSchema = z.preprocess(
	preprocessActionDefinitionInput,
	IAppBuilderActionDefinitionSchemaBase,
);

export type AppBuilderActionDefinitionSchemaOutput = z.infer<
	typeof IAppBuilderActionDefinitionSchema
>;

const IAppBuilderActionSlotEventPropsSessionSchema = z.strictObject({
	sessionId: z.string().optional(),
});

const IAppBuilderActionSlotEventPropsExportSchema =
	IAppBuilderActionSlotEventPropsSessionSchema.extend({
		name: z.string().optional(),
	});

const IAppBuilderActionSlotEventPropsSelectionSchema = z.strictObject({
	nameFilter: z.array(z.string()).min(1),
	viewportId: z.string().optional(),
	minimumSelection: z.number().optional(),
	maximumSelection: z.number().optional(),
	hover: z.boolean().optional(),
	hoverColor: z
		.union([z.string(), z.record(z.string(), JsonValueSchema), z.null()])
		.optional(),
	selectionColor: z
		.union([z.string(), z.record(z.string(), JsonValueSchema), z.null()])
		.optional(),
	availableColor: z
		.union([z.string(), z.record(z.string(), JsonValueSchema), z.null()])
		.optional(),
	occludeBySceneGeometry: z.boolean().optional(),
});

/** Pointer, click, contextmenu, appready, and custom events. No eventProps. */
const IAppBuilderPlainActionSlotSchema = z.strictObject({
	action: IAppBuilderActionDefinitionSchema,
});

const IAppBuilderSessionActionSlotSchema = z.strictObject({
	action: IAppBuilderActionDefinitionSchema,
	eventProps: z
		.strictObject({
			type: z.literal("session"),
			props: IAppBuilderActionSlotEventPropsSessionSchema,
		})
		.optional(),
});

const IAppBuilderExportActionSlotSchema = z.strictObject({
	action: IAppBuilderActionDefinitionSchema,
	eventProps: z
		.strictObject({
			type: z.literal("export"),
			props: IAppBuilderActionSlotEventPropsExportSchema,
		})
		.optional(),
});

const IAppBuilderSelectionActionSlotSchema = z.strictObject({
	action: IAppBuilderActionDefinitionSchema,
	eventProps: z.strictObject({
		type: z.literal("selection"),
		props: IAppBuilderActionSlotEventPropsSelectionSchema,
	}),
});

function actionSlotList<T extends z.ZodType>(slot: T) {
	return z.union([slot, z.array(slot)]);
}

const plainActionSlotListSchema = actionSlotList(
	IAppBuilderPlainActionSlotSchema,
);

/**
 * Known events use `shape`. Any other key must be `custom:` kebab-case and
 * a plain slot (no `eventProps`).
 */
function actionSlotsSchema<T extends z.ZodRawShape>(shape: T) {
	return z
		.object(shape)
		.catchall(plainActionSlotListSchema)
		.superRefine((value, ctx) => {
			for (const key of Object.keys(value)) {
				if (Object.prototype.hasOwnProperty.call(shape, key)) continue;
				if (APP_BUILDER_CUSTOM_EVENT_PATTERN.test(key)) continue;
				ctx.addIssue({
					code: "custom",
					path: [key],
					message: `Unsupported action slot event "${key}". Use a known event for this node or custom:kebab-case.`,
				});
			}
		});
}

const nodeActionSlotShape = {
	click: plainActionSlotListSchema.optional(),
	contextmenu: plainActionSlotListSchema.optional(),
	pointerdown: plainActionSlotListSchema.optional(),
	pointerup: plainActionSlotListSchema.optional(),
	pointerenter: plainActionSlotListSchema.optional(),
	pointerleave: plainActionSlotListSchema.optional(),
};

/** Widgets, tabs, containers, controls, toolbar items. */
const IAppBuilderNodeActionSlotsSchema = actionSlotsSchema(nodeActionSlotShape);

/**
 * Root `IAppBuilder`. Pointer/click/contextmenu are repeated here because
 * the viewport host reads them from this same object.
 */
const IAppBuilderRootActionSlotsSchema = actionSlotsSchema({
	...nodeActionSlotShape,
	appready: plainActionSlotListSchema.optional(),
	computationstart: actionSlotList(
		IAppBuilderSessionActionSlotSchema,
	).optional(),
	computationend: actionSlotList(
		IAppBuilderSessionActionSlotSchema,
	).optional(),
	computationerror: actionSlotList(
		IAppBuilderSessionActionSlotSchema,
	).optional(),
	exportstart: actionSlotList(IAppBuilderExportActionSlotSchema).optional(),
	exportend: actionSlotList(IAppBuilderExportActionSlotSchema).optional(),
	exporterror: actionSlotList(IAppBuilderExportActionSlotSchema).optional(),
	selecton: actionSlotList(IAppBuilderSelectionActionSlotSchema).optional(),
	selectoff: actionSlotList(IAppBuilderSelectionActionSlotSchema).optional(),
	hoveron: actionSlotList(IAppBuilderSelectionActionSlotSchema).optional(),
	hoveroff: actionSlotList(IAppBuilderSelectionActionSlotSchema).optional(),
});

function appBuilderNodeObject<T extends z.ZodRawShape>(shape: T) {
	return z.strictObject({
		...shape,
		actionSlots: IAppBuilderNodeActionSlotsSchema.optional(),
	});
}

// Zod type definition for IAppBuilderControlActionRef
const IAppBuilderControlActionRefSchema = z
	.strictObject({
		definition: IAppBuilderActionDefinitionSchema,
	})
	.extend(IAppBuilderActionPropsCommonSchema.shape)
	.extend(
		IAppBuilderControlPresentationSchema.pick({
			labelSide: true,
			labelAlign: true,
		}).shape,
	);

// Zod type definition for IAppBuilderControlOutputRef
const IAppBuilderControlOutputRefSchema = z
	.strictObject({
		name: z.string(),
		sessionId: z.string().optional(),
		overrides: IAppBuilderControlOutputRefOverridesSchema.optional(),
	})
	.extend(IAppBuilderControlPresentationSchema.shape);

// Zod type definition for IAppBuilderControl
const IAppBuilderControlSchema = z.discriminatedUnion("type", [
	appBuilderNodeObject({
		type: z.literal("parameter"),
		props: IAppBuilderControlParameterRefSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("export"),
		props: IAppBuilderControlExportRefSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("action"),
		props: IAppBuilderControlActionRefSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("output"),
		props: IAppBuilderControlOutputRefSchema,
	}),
]);

// Zod type definition for IAppBuilderWidgetPropsCommon
const IAppBuilderWidgetPropsCommonSchema = z.strictObject({});

// Zod type definition for IAppBuilderWidgetPropsAccordion
const IAppBuilderWidgetPropsAccordionSchema = z
	.strictObject({
		parameters: z.array(IAppBuilderParameterRefSchema).optional(),
		exports: z.array(IAppBuilderExportRefSchema).optional(),
		defaultGroupName: z.string().optional(),
	})
	.extend(IAppBuilderWidgetPropsCommonSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsText
const IAppBuilderWidgetPropsTextSchema = z
	.strictObject({
		text: z.string().optional(),
		markdown: z.string().optional(),
	})
	.extend(IAppBuilderWidgetPropsCommonSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsImage
const IAppBuilderWidgetPropsImageSchema = z
	.strictObject({
		anchor: z.string().optional(),
		alt: z.string().optional(),
		target: z.string().default("_blank"),
		isSvg: z.boolean().optional(),
	})
	.extend(IAppBuilderWidgetPropsCommonSchema.shape)
	.extend(IAppBuilderImageRefSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsRoundChart
const IAppBuilderWidgetPropsRoundChartSchema = z
	.strictObject({
		name: z.string().optional(),
		style: z.enum(["pie", "donut"]),
		labels: z.boolean().optional(),
		legend: z.boolean().optional(),
		data: z.array(
			z.strictObject({
				name: z.string(),
				value: z.number(),
				color: z.string(),
			}),
		),
	})
	.extend(IAppBuilderWidgetPropsCommonSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsChartPlotSettings
const IAppBuilderWidgetPropsChartPlotSettingsSchema = z.strictObject({
	xaxis: z.boolean().optional(),
	xlabel: z.string().optional(),
	yaxis: z.boolean().optional(),
	ylabel: z.string().optional(),
	grid: z.enum(["none", "x", "y", "xy"]).optional(),
	dots: z.boolean().optional(),
	legend: z.boolean().optional(),
});

// Zod type definition for IAppBuilderWidgetPropsChartDataSet
const IAppBuilderWidgetPropsChartDataSetSchema = z.strictObject({
	keys: z.array(z.string()),
	series: z.array(
		z.strictObject({
			name: z.string(),
			color: z.string(),
			values: z.array(z.number()),
		}),
	),
});

// Zod type definition for IAppBuilderWidgetPropsChartCommon
const IAppBuilderWidgetPropsChartCommonSchema = z
	.strictObject({
		name: z.string().optional(),
		plotSettings: IAppBuilderWidgetPropsChartPlotSettingsSchema,
		data: IAppBuilderWidgetPropsChartDataSetSchema,
	})
	.extend(IAppBuilderWidgetPropsCommonSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsLineChart
const IAppBuilderWidgetPropsLineChartSchema = z
	.strictObject({
		style: z
			.enum([
				"bump",
				"linear",
				"natural",
				"monotone",
				"step",
				"stepBefore",
				"stepAfter",
			])
			.optional(),
	})
	.extend(IAppBuilderWidgetPropsChartCommonSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsAreaChart
const IAppBuilderWidgetPropsAreaChartSchema = z
	.strictObject({
		style: z
			.enum([
				"bump",
				"linear",
				"natural",
				"monotone",
				"step",
				"stepBefore",
				"stepAfter",
			])
			.optional(),
		type: z.enum(["default", "stacked", "percent", "split"]).optional(),
	})
	.extend(IAppBuilderWidgetPropsChartCommonSchema.shape);

// Zod type definition for IAppBuilderWidgetPropsBarChart
const IAppBuilderWidgetPropsBarChartSchema = z
	.strictObject({
		style: z
			.enum(["default", "stacked", "percent", "waterfall"])
			.optional(),
	})
	.extend(IAppBuilderWidgetPropsChartCommonSchema.shape);

const IAppBuilderWidgetPropsAttributeVisualizationNumberGradientSchema =
	z.strictObject({
		type: z.literal("number"),
		min: z.number().optional(),
		max: z.number().optional(),
		steps: z.enum(ATTRIBUTE_VISUALIZATION).or(
			z.array(
				z.strictObject({
					value: z.number(),
					colorBefore: z.string(),
					colorAfter: z.string(),
				}),
			),
		),
	});

// Zod type definition for IAppBuilderWidgetPropsAttributeVisualizationStringGradient
const IAppBuilderWidgetPropsAttributeVisualizationStringGradientSchema =
	z.strictObject({
		type: z.literal("string"),
		defaultColor: z.string().optional(),
		labelColors: z.array(
			z.strictObject({
				values: z.array(z.string()),
				color: z.string(),
			}),
		),
	});

// Zod type definition for IAppBuilderWidgetPropsAttributeVisualizationGradient
const IAppBuilderWidgetPropsAttributeVisualizationGradientSchema = z.union([
	z.discriminatedUnion("type", [
		IAppBuilderWidgetPropsAttributeVisualizationNumberGradientSchema,
		IAppBuilderWidgetPropsAttributeVisualizationStringGradientSchema,
	]),
	z.enum(ATTRIBUTE_VISUALIZATION),
]);

// Zod type definition for IAppBuilderWidgetPropsAttributeVisualization
const IAppBuilderWidgetPropsAttributeVisualizationSchema = z.strictObject({
	title: z.string().optional(),
	tooltip: z.string().optional(),
	attributes: z
		.array(
			z
				.strictObject({
					attribute: z.string(),
					gradient:
						IAppBuilderWidgetPropsAttributeVisualizationGradientSchema.optional(),
				})
				.or(z.string()),
		)
		.optional(),
	visualizationMode: z.enum(AttributeVisualizationVisibility).optional(),
	showLegend: z.boolean().optional(),
	defaultGradient:
		IAppBuilderWidgetPropsAttributeVisualizationGradientSchema.optional(),
	initialAttribute: z.string().optional(),
	passiveMaterial: z
		.strictObject({
			color: z.string().optional(),
			opacity: z.number().optional(),
		})
		.optional(),
	disableAttributeAnchors: z.boolean().optional(),
});

// Zod type definition for IAppBuilderWidgetPropsActions
const IAppBuilderWidgetPropsActionsSchema = z.strictObject({
	actions: z.array(IAppBuilderLegacyActionDefinitionSchema),
});

// Zod type definition for IAppBuilderWidgetPropsAgent
const IAppBuilderWidgetPropsAgentSchema = z.strictObject({
	context: z.string().optional(),
	parameterNames: z.array(z.string()).optional(),
	parameterNamesExclude: z.array(z.string()).optional(),
});

// Zod type definition for IAppBuilderWidgetPropsHostedAgent
const IAppBuilderWidgetPropsHostedAgentSchema = z.strictObject({
	title: z.string().optional(),
	height: z.union([z.string(), z.number()]).optional(),
});

// Zod type definition for IAppBuilderWidgetPropsIframe
const IAppBuilderWidgetPropsIframeSchema = z.strictObject({
	url: z.string(),
	title: z.string().optional(),
	height: z.union([z.string(), z.number()]).optional(),
});

// Zod type definition for IAppBuilderWidgetPropsProgress
const IAppBuilderWidgetPropsProgressSchema = z.strictObject({
	showPercentage: z.boolean().optional(),
	showOnComplete: z.boolean().optional(),
	showMessages: z.boolean().optional(),
	delayRemoval: z.number().optional(),
});

// Zod type definition for IAppBuilderWidgetPropsSceneTreeExplorer
const IAppBuilderWidgetPropsSceneTreeExplorerSchema = z.strictObject({});

// Zod type definition for IAppBuilderWidgetPropsDesktopClientSelection
const IAppBuilderWidgetPropsDesktopClientSelectionSchema = z.strictObject({
	clientsFilter: z.array(z.string()).optional(),
	autoConnect: z.boolean().optional(),
});

// Zod type definition for IAppBuilderWidgetPropsDesktopClientOutputs
const IAppBuilderWidgetPropsDesktopClientOutputsSchema = z.strictObject({});

// Zod type definition for IAppBuilderWidgetPropsControls
const IAppBuilderWidgetPropsControlsSchema = z.strictObject({
	controls: z.array(IAppBuilderControlSchema),
});

// Zod type definition for IAppBuilderWidgetPropsForm
const IAppBuilderWidgetPropsFormSchema = z.strictObject({
	controls: z.array(IAppBuilderControlSchema).optional(),
	parameters: z.array(IAppBuilderParameterRefSchema).optional(),
	export: IAppBuilderControlExportRefSchema.optional(),
	submit: z.enum(FormWidgetSubmitBehavior).optional(),
	successMessage: z.string().optional(),
	errorMessage: z.string().optional(),
});

// Zod type definition for IAppBuilderWidgetPropsAccordionUi
const IAppBuilderWidgetPropsAccordionUiSchema = z.strictObject({
	items: z.array(
		z.strictObject({
			value: z.string().optional(),
			name: z.string(),
			icon: z.string().optional(),
			tooltip: z.string().optional(),
			widgets: z.array(
				z.lazy(
					(): z.ZodType<IAppBuilderWidget> => IAppBuilderWidgetSchema,
				),
			),
		}),
	),
	multiple: z.boolean().optional(),
	defaultValue: z.union([z.string(), z.array(z.string())]).optional(),
	value: z.union([z.string(), z.array(z.string())]).optional(),
});

// Zod type definition for IAppBuilderWidgetPropsStackUi
const IAppBuilderWidgetPropsStackUiSchema = z.strictObject({
	name: z.string(),
	icon: z.string().optional(),
	tooltip: z.string().optional(),
	widgets: z.array(
		z.lazy((): z.ZodType<IAppBuilderWidget> => IAppBuilderWidgetSchema),
	),
});

// Zod type definition for IAppBuilderWidgetPropsSavedStates
const savedStatesVisualizationValues = [
	"buttonflex",
	"buttongroup",
	"chipgroup",
	"dropdown",
	"imagedropdown",
	"fullwidthcards",
	"carousel",
	"grid",
] as const satisfies readonly SavedStatesVisualization[];

const IAppBuilderWidgetPropsSavedStatesSchema = z.strictObject({
	visualization: z.enum(savedStatesVisualizationValues).optional(),
});

// Zod type definition for IAppBuilderWidgetPropsTableColumn
const IAppBuilderWidgetPropsTableColumnSchema = z.strictObject({
	accessor: z.string(),
	title: z.string().optional(),
	sortable: z.boolean().optional(),
	searchable: z.boolean().optional(),
	width: z.union([z.number(), z.string()]).optional(),
});

// Zod type definition for IAppBuilderWidgetPropsTable
const IAppBuilderWidgetPropsTableSchema = z.strictObject({
	caption: z.string().optional(),
	columns: z.array(IAppBuilderWidgetPropsTableColumnSchema),
	records: z.array(z.record(z.string(), JsonValueSchema)),
	highlightOnHover: z.boolean().optional(),
	stickyHeader: z.boolean().optional(),
	striped: z.boolean().optional(),
	withColumnBorders: z.boolean().optional(),
	withRowBorders: z.boolean().optional(),
	withTableBorder: z.boolean().optional(),
	height: z.number().optional(),
	estimateRowHeight: z.number().optional(),
	overscan: z.number().optional(),
});

// Zod type definition for IAppBuilderWidget
const IAppBuilderWidgetSchema = z.discriminatedUnion("type", [
	appBuilderNodeObject({
		type: z.literal("accordion"),
		props: IAppBuilderWidgetPropsAccordionSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("text"),
		props: IAppBuilderWidgetPropsTextSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("image"),
		props: IAppBuilderWidgetPropsImageSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("roundChart"),
		props: IAppBuilderWidgetPropsRoundChartSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("lineChart"),
		props: IAppBuilderWidgetPropsLineChartSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("areaChart"),
		props: IAppBuilderWidgetPropsAreaChartSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("barChart"),
		props: IAppBuilderWidgetPropsBarChartSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("actions"),
		props: IAppBuilderWidgetPropsActionsSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("attributeVisualization"),
		props: IAppBuilderWidgetPropsAttributeVisualizationSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("agent"),
		props: IAppBuilderWidgetPropsAgentSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("hostedAgent"),
		props: IAppBuilderWidgetPropsHostedAgentSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("iframe"),
		props: IAppBuilderWidgetPropsIframeSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("progress"),
		props: IAppBuilderWidgetPropsProgressSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("desktopClientSelection"),
		props: IAppBuilderWidgetPropsDesktopClientSelectionSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("desktopClientOutputs"),
		props: IAppBuilderWidgetPropsDesktopClientOutputsSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("controls"),
		props: IAppBuilderWidgetPropsControlsSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("form"),
		props: IAppBuilderWidgetPropsFormSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("accordionUi"),
		props: IAppBuilderWidgetPropsAccordionUiSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("sceneTreeExplorer"),
		props: IAppBuilderWidgetPropsSceneTreeExplorerSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("stackUi"),
		props: IAppBuilderWidgetPropsStackUiSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("savedStates"),
		props: IAppBuilderWidgetPropsSavedStatesSchema,
	}),
	appBuilderNodeObject({
		type: z.literal("table"),
		props: IAppBuilderWidgetPropsTableSchema,
	}),
]);

// Zod type definition for IAppBuilderTab
const IAppBuilderTabSchema = appBuilderNodeObject({
	name: z.string(),
	icon: z.string().optional(),
	tooltip: z.string().optional(),
	widgets: z.array(IAppBuilderWidgetSchema),
}).extend(IAppBuilderWidgetPropsCommonSchema.shape);

// Local strict schema mirroring ISelectionParameterProps from @shapediver/viewer.session.
// The external JsonSchema uses "strip" mode, so we define our own strict version to reject unknown keys.
// Colors match viewer `InteractionEffect` (string, material record, or null).
const SelectionColorSchema = z.union([
	z.string(),
	z.record(z.string(), JsonValueSchema),
	z.null(),
]);
const ISelectionParameterPropsSchema = z.strictObject({
	maximumSelection: z.number().optional(),
	minimumSelection: z.number().optional(),
	nameFilter: z.array(z.string()).optional(),
	selectionColor: SelectionColorSchema.optional(),
	availableColor: SelectionColorSchema.optional(),
	deselectOnEmpty: z.boolean().optional(),
	hover: z.boolean().optional(),
	hoverColor: SelectionColorSchema.optional(),
	occludeBySceneGeometry: z.boolean().optional(),
	prompt: z
		.strictObject({
			inactiveTitle: z.string().optional(),
			activeTitle: z.string().optional(),
			activeText: z.string().optional(),
		})
		.optional(),
	buttons: z
		.strictObject({
			clear: z.boolean().optional(),
		})
		.optional(),
	activeMode: z.enum(["default", "activeOnStart", "alwaysActive"]).optional(),
	presentation: z.enum(["widget", "toolbar"]).optional(),
});

// Zod type definition for IAppBuilderAnchor3dContainerProperties
const IAppBuilderAnchor3dContainerPropertiesSchema = z.strictObject({
	id: z.string(),
	location: z.tuple([z.number(), z.number(), z.number()]),
	allowPointerEvents: z.boolean().optional(),
	justification: z.enum(TAG3D_JUSTIFICATION).optional(),
	previewIcon: z.string().optional(),
	width: z.union([z.string(), z.number()]).optional(),
	height: z.union([z.string(), z.number()]).optional(),
	maxWidth: z.union([z.string(), z.number()]).optional(),
	maxHeight: z.union([z.string(), z.number()]).optional(),
	useContainer: z.boolean().optional(),
	useCloseButton: z.boolean().optional(),
	closingStrategy: z.enum(["button", "emptyClick"]).optional(),
	hideable: z.boolean().optional(),
	exclusive: z.boolean().optional(),
	defaultOpen: z.boolean().optional(),
	selectionProperties: ISelectionParameterPropsSchema.optional(),
	mobileFallback: appBuilderViewportAnchorMobileFallbackSchema.optional(),
});

// Zod type definition for IAppBuilderAnchor2dContainerProperties
const IAppBuilderAnchor2dContainerPropertiesSchema = z.strictObject({
	id: z.string(),
	location: z
		.union([
			z.tuple([z.string(), z.string()]),
			z.tuple([z.number(), z.number()]),
		])
		.optional(),
	allowPointerEvents: z.boolean().optional(),
	justification: z.enum(TAG3D_JUSTIFICATION).optional(),
	previewIcon: z.string().optional(),
	useCloseButton: z.boolean().optional(),
	closingStrategy: z.enum(["button", "emptyClick"]).optional(),
	exclusive: z.boolean().optional(),
	defaultOpen: z.boolean().optional(),
	draggable: z.boolean().optional(),
	width: z.union([z.string(), z.number()]).optional(),
	height: z.union([z.string(), z.number()]).optional(),
	maxWidth: z.union([z.string(), z.number()]).optional(),
	maxHeight: z.union([z.string(), z.number()]).optional(),
	useContainer: z.boolean().optional(),
	selectionProperties: ISelectionParameterPropsSchema.optional(),
	mobileFallback: appBuilderViewportAnchorMobileFallbackSchema.optional(),
});

const IAppBuilderToolbarItemMetaShape = {
	id: z.string().optional(),
	order: z.number().optional(),
	presentation: z.enum(["button", "item"]).optional(),
	actionSlots: IAppBuilderNodeActionSlotsSchema.optional(),
};

const IAppBuilderToolbarControlItemSchema = z.discriminatedUnion("type", [
	z.strictObject({
		...IAppBuilderToolbarItemMetaShape,
		type: z.literal("parameter"),
		props: IAppBuilderControlParameterRefSchema,
	}),
	z.strictObject({
		...IAppBuilderToolbarItemMetaShape,
		type: z.literal("export"),
		props: IAppBuilderControlExportRefSchema,
	}),
	z.strictObject({
		...IAppBuilderToolbarItemMetaShape,
		type: z.literal("action"),
		props: IAppBuilderControlActionRefSchema,
	}),
	z.strictObject({
		...IAppBuilderToolbarItemMetaShape,
		type: z.literal("output"),
		props: IAppBuilderControlOutputRefSchema,
	}),
]);

const IAppBuilderToolbarActionItemSchema = z.strictObject({
	...IAppBuilderToolbarItemMetaShape,
	type: z.literal("action"),
	props: IAppBuilderControlActionRefSchema,
});

const IAppBuilderToolbarItemSchema = z.discriminatedUnion("type", [
	...IAppBuilderToolbarControlItemSchema.options,
	z.strictObject({
		...IAppBuilderToolbarItemMetaShape,
		type: z.literal("actionMenu"),
		props: z.strictObject({
			...IAppBuilderControlPresentationSchema.shape,
			sections: z.array(z.array(IAppBuilderToolbarActionItemSchema)),
		}),
	}),
	z.strictObject({
		...IAppBuilderToolbarItemMetaShape,
		type: z.literal("widgets"),
		props: z.strictObject({
			...IAppBuilderControlPresentationSchema.shape,
			widgets: z.array(IAppBuilderWidgetSchema),
		}),
	}),
	z.strictObject({
		...IAppBuilderToolbarItemMetaShape,
		type: z.literal("tabs"),
		props: z.strictObject({
			...IAppBuilderControlPresentationSchema.shape,
			tabs: z.array(IAppBuilderTabSchema),
			stickyTabs: z.boolean().optional(),
		}),
	}),
]);

const IAppBuilderToolbarContainerPropertiesSchema = z.strictObject({
	id: z.string(),
	side: z.enum(["top", "bottom", "left", "right"]).optional(),
	align: z.enum(["start", "center", "end"]).optional(),
	order: z.number().optional(),
	visibility: z.enum(["always", "onMouseActivity"]).optional(),
	defaultOpen: z.boolean().optional(),
	exclusiveContainers: z.boolean().optional(),
});

// Zod type definition for IAppBuilderContainer
const IAppBuilderContainerSchema = z.discriminatedUnion("name", [
	appBuilderNodeObject({
		name: z.literal(AppBuilderContainerNameType.Anchor3d),
		props: IAppBuilderAnchor3dContainerPropertiesSchema,
		stickyTabs: z.boolean().optional(),
		tabs: z.array(IAppBuilderTabSchema).optional(),
		widgets: z.array(IAppBuilderWidgetSchema).optional(),
	}).extend(IAppBuilderWidgetPropsCommonSchema.shape),
	appBuilderNodeObject({
		name: z.literal(AppBuilderContainerNameType.Anchor2d),
		props: IAppBuilderAnchor2dContainerPropertiesSchema,
		stickyTabs: z.boolean().optional(),
		tabs: z.array(IAppBuilderTabSchema).optional(),
		widgets: z.array(IAppBuilderWidgetSchema).optional(),
	}).extend(IAppBuilderWidgetPropsCommonSchema.shape),
	appBuilderNodeObject({
		name: z.literal(AppBuilderContainerNameType.Toolbar),
		props: IAppBuilderToolbarContainerPropertiesSchema,
		groups: z.array(z.array(IAppBuilderToolbarItemSchema)).optional(),
	}).extend(IAppBuilderWidgetPropsCommonSchema.shape),
	appBuilderNodeObject({
		name: z.enum([
			AppBuilderContainerNameType.Left,
			AppBuilderContainerNameType.Right,
			AppBuilderContainerNameType.Bottom,
			AppBuilderContainerNameType.Top,
		]),
		props: appBuilderStandardContainerPropsSchema.optional(),
		stickyTabs: z.boolean().optional(),
		tabs: z.array(IAppBuilderTabSchema).optional(),
		widgets: z.array(IAppBuilderWidgetSchema).optional(),
	}).extend(IAppBuilderWidgetPropsCommonSchema.shape),
]);

const IAppBuilderOutputActionsPropsSetParameterValueSchema = z.strictObject({
	parameter: z.string(),
	output: z.string(),
});

// Zod type definition for IAppBuilderSessionDefinitionBase
const IAppBuilderSessionDefinitionBaseSchema = z.strictObject({
	sessionId: z
		.string()
		.describe(
			"Id of the session. When slug is omitted, this id is used as the platform model slug.",
		),
	slug: z
		.string()
		.optional()
		.describe(
			"Platform model slug used to resolve the session. When omitted, sessionId is used as the slug.",
		),
});

// Zod type definition for IAppBuilderSessionDefinition
const IAppBuilderSessionDefinitionSchema =
	IAppBuilderSessionDefinitionBaseSchema.extend({
		initialParameterValues: z
			.record(z.string(), z.string())
			.optional()
			.describe(
				"Optional initial parameter values, matching theme sessions. The key is the displayname, name, or id of the parameter.",
			),
	});

// Zod type definition for IAppBuilderInstances
const IAppBuilderInstancesSchema =
	IAppBuilderSessionDefinitionBaseSchema.extend({
		parameterValues: z
			.record(
				z.string(),
				z
					.string()
					.or(z.number())
					.or(z.boolean())
					.or(IAppBuilderParameterValueSourceDefinitionSchema),
			)
			.optional(),
		name: z.string().optional(),
		transformations: z.array(z.array(z.number())).optional(),
		outputActions: z
			.array(
				z.discriminatedUnion("type", [
					z.strictObject({
						type: z.literal("setParameterValue"),
						props: IAppBuilderOutputActionsPropsSetParameterValueSchema,
					}),
				]),
			)
			.optional(),
	});

// Zod type definition for FilterValue ("include" | "exclude")
const FilterValueSchema = z.enum(["include", "exclude"]);

// Zod type definition for IAgentParameterRef
const IAgentParameterRefSchema = z.strictObject({
	name: z
		.string()
		.describe(
			"Id or name or displayname of the referenced parameter (in that order).",
		),
	sessionId: z
		.string()
		.optional()
		.describe(
			"Optional id of the session the referenced parameter belongs to.",
		),
	description: z
		.string()
		.optional()
		.describe(
			"Optional description of the parameter, providing further context to the agent.",
		),
});

// Zod type definition for IAgentActionControlRef.action (id required)
const IAgentEmbeddedActionSchema = IAppBuilderControlActionRefSchema.extend({
	id: z.string(),
});

// Zod type definition for IAgentActionControlRef
const IAgentActionControlRefSchema = z.strictObject({
	name: z
		.string()
		.optional()
		.describe(
			"Id or label (in that order) of the action control that should be referenced. This considers all action controls available anywhere in the App Builder output, which are part of some controls widget.",
		),
	action: IAgentEmbeddedActionSchema.optional().describe(
		"Optional embedded action control definition. If this is provided, the name property will be ignored.",
	),
	description: z
		.string()
		.optional()
		.describe(
			"Optional description of the action, providing further context to the agent.",
		),
});

// Zod type definition for AppBuilderActionType
const AppBuilderActionTypeSchema = z.enum(AppBuilderActionType);

/**
 * The "list_action_controls" tool exposes action *controls* (UI elements that
 * trigger actions), not the underlying actions themselves.
 * Depending on the action definition, triggering may show UI (e.g. a modal).
 * For headless use of underlying actions, define further generic tools or
 * specific tool definitions.
 */
// Zod type definition for GenericToolSettings
const GenericToolSettingsSchema = z.discriminatedUnion("name", [
	z.strictObject({
		name: z.literal(GenericToolName.ListParameterDefinitions),
		parameters: z
			.array(IAgentParameterRefSchema)
			.optional()
			.describe(
				"Optional list of parameters that should be exposed to the agent. In case this list is not provided, parameters will be filtered based on the filter property.",
			),
		filter: z
			.strictObject({
				hidden: FilterValueSchema.optional().describe(
					'Whether to include parameters whose "hidden" property is true. Defaults to "exclude" if not provided.',
				),
				invisible: FilterValueSchema.optional().describe(
					'Whether to include parameters that are currently not exposed in the UI (not referenced by some parameter control or accordion widget). This filter applies on top of the "hidden" filter. Defaults to "include" if not provided.',
				),
				sessionIds: z
					.array(z.string())
					.optional()
					.describe(
						"Which sessions' parameters should be exposed to the agent. If not provided, parameters of the controller session will be exposed.",
					),
			})
			.optional()
			.describe(
				"Optional filter for parameters that should be exposed to the agent. Ignored if the parameters property is provided.",
			),
	}),
	z.strictObject({
		name: z.literal(GenericToolName.GetParameterValues),
	}),
	z.strictObject({
		name: z.literal(GenericToolName.SetParameterValues),
	}),
	z.strictObject({
		name: z.literal(GenericToolName.ListActionControls),
		actions: z
			.array(IAgentActionControlRefSchema)
			.optional()
			.describe(
				"Optional list of actions that should be exposed to the agent. In case this list is not provided, actions will be filtered based on the filter property. The filter will be applied to all actions available anywhere in the App Builder output, as well as to actions available via default toolbars.",
			),
		filter: z
			.strictObject({
				types: z
					.array(AppBuilderActionTypeSchema)
					.optional()
					.describe(
						"The types of actions that should be exposed to the agent. Defaults to DefaultListActionControlType.",
					),
			})
			.optional()
			.describe(
				"Optional filter for actions that should be exposed to the agent. Ignored if the actions property is provided.",
			),
	}),
	z.strictObject({
		name: z.literal(GenericToolName.TriggerActionControl),
	}),
	z.strictObject({
		name: z.literal(GenericToolName.SetCamera),
	}),
	z.strictObject({
		name: z.literal(GenericToolName.GetCamera),
	}),
	z.strictObject({
		name: z.literal(GenericToolName.GetScreenshot),
	}),
	z.strictObject({
		name: z.literal(GenericToolName.AskUserQuestion),
	}),
	z.strictObject({
		name: z.literal(GenericToolName.GetMetric),
	}),
]);

/**
 * Settings of a tool to be executed remotely, typically by an API call,
 * the Agent2Agent protocol, model context protocol (MCP), etc.
 * To be defined.
 */
// Zod type definition for RemoteToolExecutionSettings
const RemoteToolExecutionSettingsSchema = z.strictObject({});

// Zod type definition for SpecificToolSettings
const SpecificToolSettingsSchema = z.strictObject({
	name: z.string().describe("Name of the tool. Use snake case."),
	description: z
		.string()
		.optional()
		.describe(
			"Optional description of the tool, providing context to the agent.",
		),
	inputSchema: z
		.record(z.string(), JsonValueSchema)
		.describe(
			"JSON Schema for the tool call. Passed through to the agent as inputSchema. The call is validated against this schema before the action runs.",
		),
	action: IAppBuilderActionDefinitionSchema.optional().describe(
		'Optional action that runs when the tool is triggered. Information about this action is not exposed to the agent. Use executeActions when several actions should run. A value from the tool call can be mapped onto setParameterValue and setParameterValues, including inside executeActions, using the agentTool parameter value source. A missing action fails with "no action".',
	),
	remoteExecution: RemoteToolExecutionSettingsSchema.optional().describe(
		"Optional remote execution settings for the tool. Not implemented. Ignored.",
	),
});

// Zod type definition for IAppBuilderAgent
const IAppBuilderAgentSchema = z.strictObject({
	id: z.string().describe("Unique identifier of the agent."),
	name: z
		.string()
		.describe("Display name of the agent (exposed to the user)."),
	message: z.string().describe("The agent's system prompt."),
	profileName: z
		.string()
		.optional()
		.describe(
			"Optional name of the settings profile to use for this agent. Leave empty to use the default profile.",
		),
	useGenericToolDefaults: z
		.boolean()
		.optional()
		.describe(
			"Boolean indicating whether all available generic tools shall be exposed using their default settings. Default is true. If this is set to true, settings for individual generic tools can be overridden by including them in the genericTools property. If this is set to false, only the generic tools included in the genericTools property will be available to the agent.",
		),
	genericTools: z
		.array(GenericToolSettingsSchema)
		.optional()
		.describe(
			"Settings of the generic tools that should be available to the agent.",
		),
	specificTools: z
		.array(SpecificToolSettingsSchema)
		.optional()
		.describe(
			"Definition of specific tools that should be available to the agent.",
		),
});

export type GenericToolSettingsSchemaOutput = z.infer<
	typeof GenericToolSettingsSchema
>;

export type AppBuilderAgentSchemaOutput = z.infer<
	typeof IAppBuilderAgentSchema
>;

// Zod type definition for IAppBuilder
const IAppBuilderSchema = z.strictObject({
	version: z.literal("1.0"),
	parameters: z.array(IAppBuilderParameterDefinitionSchema).optional(),
	sessionId: z.string().optional(),
	containers: z.array(IAppBuilderContainerSchema),
	instances: z.array(IAppBuilderInstancesSchema).optional(),
	sessions: z.array(IAppBuilderSessionDefinitionSchema).optional(),
	agents: z.array(IAppBuilderAgentSchema).optional(),
	actionSlots: IAppBuilderRootActionSlotsSchema.optional(),
});

export type AppBuilderSchemaOutput = z.infer<typeof IAppBuilderSchema>;

export const validateAppBuilder = (value: any) => {
	return IAppBuilderSchema.safeParse(value);
};

// Zod type definition for IAppBuilderSettingsSession
const IAppBuilderSettingsSessionSchema = z.strictObject({
	ticket: z.string().optional(),
	guid: z.string().optional(),
	modelViewUrl: z.string().optional(),
	jwtToken: z.string().optional(),
	id: z.string(),
	waitForOutputs: z.boolean().optional(),
	loadOutputs: z.boolean().optional(),
	excludeViewports: z.array(z.string()).optional(),
	initialParameterValues: z.record(z.string(), z.string()).optional(),
	slug: z.string().optional(),
	platformUrl: z.string().optional(),
	acceptRejectMode: z.boolean().optional(),
	modelStateId: z.string().optional(),
	instance: z.boolean().optional(),
	loadOnFirstUse: z.boolean().optional(),
	keepInStore: z.boolean().optional(),
	hideDefaultToolbar: z.boolean().optional(),
});

// Zod type definition for IAppBuilderSettingsSettings
const IAppBuilderSettingsSettingsSchema = z.strictObject({
	disableFallbackUi: z.boolean().optional(),
});

// Zod type definition for IAppBuilderSettingsJson
const IAppBuilderSettingsJsonSchemaBase = z.strictObject({
	version: z.literal("1.0"),
	sessions: z.array(IAppBuilderSettingsSessionSchema).optional(),
	settings: IAppBuilderSettingsSettingsSchema.optional(),
	themeOverrides: MantineThemeOverrideSchema.optional(),
	appBuilderOverride: IAppBuilderSchema.optional(),
	agentOverride: z.array(IAppBuilderAgentSchema).optional(),
});

const IAppBuilderSettingsJsonSchema =
	IAppBuilderSettingsJsonSchemaBase.superRefine((data, ctx) => {
		const components = data.themeOverrides?.components as
			| Record<string, {defaultProps?: unknown}>
			| undefined;
		if (!components || typeof components !== "object") return;

		validateThemeComponentsRecord(components, ctx, [
			"themeOverrides",
			"components",
		]);
	});

export type AppBuilderSettingsJsonSchemaOutput = z.infer<
	typeof IAppBuilderSettingsJsonSchema
>;

export const validateAppBuilderSettingsJson = (value: any) => {
	return IAppBuilderSettingsJsonSchema.safeParse(value);
};

/** Zod 4 — human-readable paths and messages for AppBuilder / settings JSON validation. */
export function formatAppBuilderZodError(
	error: Parameters<typeof prettifyError>[0],
): string {
	return prettifyError(error);
}
